import React, { createContext, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ConfirmOptions {
  title: string;
  description: string;
  /** Defaults to common.confirm. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm action as destructive. On by default. */
  destructive?: boolean;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * One AlertDialog for the whole app, driven imperatively.
 *
 * Most destructive actions here live in hooks and event handlers rather than in
 * JSX, so a declarative per-site dialog would mean threading open/target state
 * through every one of them. `await confirm(...)` keeps the guard on the line
 * that does the damage, where it cannot drift away from it.
 */
const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (next) =>
      new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setOptions(next);
      }),
    [],
  );

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      <AlertDialog
        open={options !== null}
        // Escape and the overlay resolve as a cancel, never as a silent drop:
        // an unresolved promise would leave the caller awaiting forever.
        onOpenChange={(open) => !open && settle(false)}
      >
        {options && (
          <AlertDialogContent className="revy-ui">
            <AlertDialogHeader>
              <AlertDialogTitle className="revy-cjk leading-[1.5]">{options.title}</AlertDialogTitle>
              <AlertDialogDescription className="revy-cjk leading-[1.6]">
                {options.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => settle(false)}>
                {options.cancelLabel ?? t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => settle(true)}
                className={cn(
                  options.destructive !== false &&
                    buttonVariants({ variant: 'destructive' }),
                )}
              >
                {options.confirmLabel ?? t('common.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
